/**
 * Teslyn Cloud's calls for the Height breach report's entries (R197, decision 1017; docs/specs/geosoft-qc.md §28;
 * 20261104000000_height_breach.sql). The entries are added on Teslyn Field laptops and synced; here the project's
 * staff read them (RLS: tenant admins, managers, field crew, office staff and data processors; never clients or
 * pilots), with each entry's chart from the private bucket height-breach through a 5-minute signed link. The Word
 * document and PDF stay in the project folder, written by the laptops.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { BREACH_CHART_BUCKET, type HeightBreachEntry } from '@teslyn/domain';
import { missingView, plainPortalError } from '../portal/api.ts';

const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export interface ProjectHeightBreaches {
  readonly entries: readonly HeightBreachEntry[];
  /** Each entry's chart in Teslyn Cloud (project_file kind breach_chart), by entry id; none until a laptop sent it. */
  readonly charts: Readonly<Record<string, string>>;
}

/**
 * Every entry of the project, removed ones too, with where each chart is. Null when Teslyn Cloud does not have the
 * entries yet (20261104000000 not applied).
 */
export async function loadProjectHeightBreaches(
  sb: SupabaseClient,
  projectId: string,
): Promise<ProjectHeightBreaches | null> {
  const [e, f] = await Promise.all([
    sb.from('height_breach').select('*').eq('project_id', projectId),
    sb
      .from('project_file')
      .select('record_id, object_path')
      .eq('project_id', projectId)
      .eq('kind', 'breach_chart'),
  ]);
  if (missingView(e.error)) return null;
  if (e.error) throw new Error(plainPortalError(e.error));
  const charts: Record<string, string> = {};
  for (const r of arr<{ record_id: string; object_path: string }>(f.error ? [] : f.data))
    charts[r.record_id] = r.object_path;
  const entries = arr<HeightBreachEntry>(e.data).map((x): HeightBreachEntry => ({
    ...x,
    history: arr<HeightBreachEntry['history'][number]>(x.history),
  }));
  return { entries, charts };
}

/** A 5-minute link to an entry's chart. */
export async function breachChartUrl(sb: SupabaseClient, path: string): Promise<string> {
  const { data, error } = await sb.storage.from(BREACH_CHART_BUCKET).createSignedUrl(path, 300);
  if (error || !data) throw new Error(error ? plainPortalError(error) : 'The chart could not be opened.');
  return data.signedUrl;
}
